import {Injectable} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {catchError, switchMap} from 'rxjs/operators';
import {from, of} from 'rxjs';
import {LoadError} from '@app/core/store/actions/error.actions';
import {
	ApproveSickDay, ApproveSickDaySuccess, DeleteSickDay, DeleteSickDaySuccess,
	ESickDaysActions, GetSickDayAnswers, GetSickDayAnswersSuccess,
	GetSickDays,
	GetSickDaysSuccess,
	OpenCreateSickDayModal, OpenEmployeeSickDayAnswerModal,
	OpenSickDayAnswersModal,
	PostSickDay,
	PostSickDaySuccess, RespondSickDay, RespondSickDaySuccess
} from '@app/core/store/actions/sick-days.actions';
import {SickDaysService} from '@app/core/services/api/sick-days/sick-days.service';
import {ISickDay} from '@app/core/interfaces/sick-days.interface';
import {MatDialog} from '@angular/material/dialog';
import {CreateSickDayComponent} from '@app/modules/shared/components/sick-days/create-sick-day/create-sick-day.component';
import {PaginationHolder} from '@app/core/models/pagination.model';
import {SickDayAnswersComponent} from '@app/modules/shared/components/sick-days/sick-day-answers/sick-day-answers.component';
import {SickDayAnswerEmployeeComponent} from '@app/modules/shared/components/sick-days/sick-day-answer-employee/sick-day-answer-employee.component';
import {ISickDayResponse} from '@app/core/interfaces/i-sick-day-response';

@Injectable()
export class SickDaysEffects {

	@Effect()
	public onGetSickDays$ = this.actions$.pipe(
		ofType<GetSickDays>(ESickDaysActions.GetSickDays),
		switchMap((action: GetSickDays) => from(this.sickDaysService.getSickDays()).pipe(
			switchMap((res: PaginationHolder<ISickDay>) => [
				new GetSickDaysSuccess(res.data, res.pagination),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onPostSickDay$ = this.actions$.pipe(
		ofType<PostSickDay>(ESickDaysActions.PostSickDay),
		switchMap((action: PostSickDay) => from(this.sickDaysService.postSickDay(action.payload)).pipe(
			switchMap((sickDay: ISickDay) => {
				this.dialog.getDialogById('createSickMessageDlg')?.close();

				return [
					new PostSickDaySuccess(sickDay),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onApproveSickDay$ = this.actions$.pipe(
		ofType<ApproveSickDay>(ESickDaysActions.ApproveSickDay),
		switchMap((action: ApproveSickDay) => from(this.sickDaysService.approveSickDay(action.sickDay, action.userId)).pipe(
			switchMap((sickDay: ISickDay) => {
				this.dialog.getDialogById('sickDayAnswersDlg')?.close();

				return [
					new ApproveSickDaySuccess(sickDay),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onDeleteSickDay$ = this.actions$.pipe(
		ofType<DeleteSickDay>(ESickDaysActions.DeleteSickDay),
		switchMap((action: DeleteSickDay) => from(this.sickDaysService.deleteSickDay(action.sickDay)).pipe(
			switchMap((sickDay: ISickDay) =>
				[
					new DeleteSickDaySuccess(sickDay),
				]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onRespondSickDay$ = this.actions$.pipe(
		ofType<RespondSickDay>(ESickDaysActions.RespondSickDay),
		switchMap((action: RespondSickDay) => from(this.sickDaysService.respondSickDay(action.sickDay, action.response)).pipe(
			switchMap((response: ISickDayResponse) => {
				this.dialog.getDialogById('employeeSickDayAnswerDlg')?.close();

				return [
					new RespondSickDaySuccess(response),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetSickDayAnswers$ = this.actions$.pipe(
		ofType<GetSickDayAnswers>(ESickDaysActions.GetSickDayAnswers),
		switchMap((action: GetSickDayAnswers) => from(this.sickDaysService.getSickDayAnswers(action.sickDay)).pipe(
			switchMap((answers: any) =>
				[
					new GetSickDayAnswersSuccess(answers),
				]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onOpenCreateSickDayModal$ = this.actions$.pipe(
		ofType<OpenCreateSickDayModal>(ESickDaysActions.OpenCreateSickDayModal),
		switchMap((action: OpenCreateSickDayModal) => {
			this.dialog.open(CreateSickDayComponent, {
				data: {
					date: action.date,
					shiftId: action.shiftId,
				},
				id: 'createSickMessageDlg',
				panelClass: 'fullscreen-dialog-max-s',
			});

			return [];
		}),
	);

	@Effect()
	public onOpenSickDayAnswersModal$ = this.actions$.pipe(
		ofType<OpenSickDayAnswersModal>(ESickDaysActions.OpenSickDayAnswersModal),
		switchMap((action: OpenSickDayAnswersModal) => {
			this.dialog.open(SickDayAnswersComponent, {
				data: {
					sickDay: action.sickDay,
				},
				id: 'sickDayAnswersDlg',
				panelClass: 'fullscreen-dialog-max-s',
			});

			return [];
		}),
	);

	@Effect()
	public onOpenEmployeeSickDayAnswerModal$ = this.actions$.pipe(
		ofType<OpenEmployeeSickDayAnswerModal>(ESickDaysActions.OpenEmployeeSickDayAnswerModal),
		switchMap((action: OpenEmployeeSickDayAnswerModal) => {
			this.dialog.open(SickDayAnswerEmployeeComponent, {
				data: {
					sickDay: action.sickDay,
				},
				id: 'employeeSickDayAnswerDlg',
				panelClass: 'fullscreen-dialog-max-s',
			});

			return [];
		}),
	);

	constructor(
		private actions$: Actions,
		private sickDaysService: SickDaysService,
		private dialog: MatDialog,
	) {
	}
}
